import type {
	FilterableListFilter,
	FilterableListInitialFlags,
	FilterableListItems,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';

export type FiltersList = Record<string, string[]>;

/** Keys of `FilterableListItems` whose value is a string array (e.g. `themeGuids`). */
export type ArrayKeys = {
	[K in keyof FilterableListItems]-?: NonNullable<
		FilterableListItems[K]
	> extends string[]
		? K
		: never;
}[keyof FilterableListItems];

export type StringKeys = {
	[K in keyof FilterableListItems]-?: NonNullable<
		FilterableListItems[K]
	> extends string
		? K
		: never;
}[keyof FilterableListItems];

export type InitialListItemKey = ArrayKeys | StringKeys;

const NUMERIC_FILTER_MIN_VALUE = 1;

/**
 * Drops active filters the given definitions don't support: unknown group
 * titles, values not present in the group's items, and non-canonical or
 * out-of-range numeric values. Key and value order of `active` is preserved,
 * so a fully valid map comes back structurally equal to its input.
 *
 * `trusted` filters (the plugin's preselection) are always valid: the BFF
 * honours them even when it omits their group from the response, e.g.
 * `countries` under `themeGuid`.
 */
export function pruneFilters(
	active: FiltersList,
	definitions: FilterableListFilter[],
	trusted: FiltersList = {},
): FiltersList {
	const pruned: FiltersList = {};

	for (const [title, values] of Object.entries(active)) {
		const definition = definitions.find((def) => def.title === title);
		const validValues = pruneGroupValues(
			values,
			definition,
			trusted[title],
		);
		if (validValues.length) {
			pruned[title] = validValues;
		}
	}

	return pruned;
}

function pruneGroupValues(
	values: string[],
	definition: FilterableListFilter | undefined,
	trustedValues: string[] | undefined,
): string[] {
	if (!definition) {
		return trustedValues ? values : [];
	}

	if (definition.type === 'numeric') {
		return pruneNumericValue(values[0], definition.maxValue);
	}

	const codes = new Set(definition.data?.map((item) => item.code));
	const validValues = values.filter(
		(value) => codes.has(value) || trustedValues?.includes(value),
	);

	// A radio group holds one value: `?nights=2,3` keeps the first
	return definition.type === 'radio' ? validValues.slice(0, 1) : validValues;
}

function pruneNumericValue(value: string, maxValue?: number): string[] {
	const numericValue = /^(0|[1-9]\d*)$/.test(value)
		? Number(value)
		: Number.NaN;
	const inRange =
		numericValue >= NUMERIC_FILTER_MIN_VALUE &&
		numericValue <= (maxValue ?? Infinity);

	return inRange ? [value] : [];
}

// Keys must match a filter group title in the API response: they are seeded
// as active filters instead of fixed request params, so the visitor can
// deselect them.
const PRESELECTABLE_FILTER_KEYS: Partial<Record<ArrayKeys, string>> = {
	countries: 'countries',
	hotelFacilities: 'hotel-facilities',
	roomFacilities: 'room-facilities',
};

export function preselectedFilters(
	items: FilterableListItems | null,
): FiltersList {
	const filters: FiltersList = {};
	const initialKeys = initialListItemKeys(items);

	for (const [key, title] of Object.entries(PRESELECTABLE_FILTER_KEYS) as [
		ArrayKeys,
		string,
	][]) {
		// A flagged key is the data set itself, not a removable preselection.
		if (initialKeys.has(key)) continue;
		const values = items?.[key] ?? [];
		if (values.length) {
			filters[title] = [...values];
		}
	}

	return filters;
}
// Keys that should be always prefixed as `initial<Name>` request params
export const INITIAL_LIST_ITEM_KEYS: readonly InitialListItemKey[] = [
	'hotelGuids',
	'themeGuids',
	'themeGuid',
	'roomType',
];

/**
 * Always-initial keys plus the ones the editor flagged in the plugin
 * (`initial: { countries: true }`).
 */
export function initialListItemKeys(
	items: FilterableListItems | null,
): ReadonlySet<InitialListItemKey> {
	const flags = items?.initial ?? {};
	const flagged = (
		Object.keys(flags) as (keyof FilterableListInitialFlags)[]
	).filter((key) => flags[key] === true);

	return new Set([...INITIAL_LIST_ITEM_KEYS, ...flagged]);
}

export function toInitialParamKey(key: string): string {
	return `initial${key.charAt(0).toUpperCase()}${key.slice(1)}`;
}

/** `initial<Name>` is reserved for plugin data; a filter may never use it. */
export function isInitialParamKey(key: string): boolean {
	return /^initial[A-Z]/.test(key);
}

export function initialListItemParams(
	items: FilterableListItems | null,
	keys: Iterable<InitialListItemKey>,
): Record<string, string> {
	const params: Record<string, string> = {};

	for (const key of keys) {
		const value = items?.[key];
		const serialized = Array.isArray(value) ? value.join(',') : value;
		if (serialized) {
			params[toInitialParamKey(key)] = serialized;
		}
	}

	return params;
}
